import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Pencil, Plus } from "lucide-react";
import { fmtEur, fmtMwhc } from "@/lib/format";
import { PartnerDialog, SECTORS } from "@/components/admin/PartnerDialog";

const LABEL = Object.fromEntries(SECTORS.map((s) => [s.code, s.label]));

const Swatch = ({ color }) => <span className="h-4 w-4 rounded-full ring-1 ring-black/10" style={{ background: color }} />;

const StatusToggle = ({ partner, onChange }) => (
  <label className="flex items-center gap-2 text-sm cursor-pointer">
    <Switch checked={partner.active} onCheckedChange={onChange} data-testid={`partner-${partner.slug}-status-switch`} />
    <span className={partner.active ? "font-medium text-emerald-700" : "font-medium text-destructive"} data-testid={`partner-${partner.slug}-status-label`}>
      {partner.active ? "Actif" : "Désactivé"}
    </span>
  </label>
);

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg bg-secondary/60 px-3 py-2 min-w-0">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="metric font-bold text-sm truncate" data-testid={testid}>{value}</div>
  </div>
);

const PartnerCard = ({ partner, onEdit, onToggle }) => (
  <Card className={`p-5 space-y-4 transition-opacity ${partner.active ? "" : "opacity-70 border-dashed"}`} data-testid={`partner-card-${partner.slug}`}>
    <div className="flex items-center gap-4">
      <div className="h-16 w-28 rounded-lg border border-border bg-white grid place-items-center overflow-hidden shrink-0">
        {partner.logo_url ? <img src={partner.logo_url} alt={partner.name} className="max-h-14 max-w-[100px] object-contain" /> : <span className="text-xs text-muted-foreground">Sans logo</span>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-heading font-bold text-lg truncate">{partner.name}</span>
          {!partner.has_active_pricing && (
            <Badge className="bg-destructive hover:bg-destructive text-white shrink-0 gap-1" data-testid={`partner-${partner.slug}-missing-price-badge`}>
              <AlertTriangle className="h-3 w-3" /> Tarif manquant
            </Badge>
          )}
        </div>
        <div className="text-xs text-muted-foreground">Identifiant : {partner.slug}</div>
        <div className="flex items-center gap-1.5 mt-1.5"><Swatch color={partner.primary_color} /><Swatch color={partner.accent_color} /></div>
      </div>
      <Button variant="outline" size="sm" onClick={onEdit} data-testid={`partner-${partner.slug}-edit-btn`}><Pencil className="h-4 w-4 mr-1.5" /> Modifier</Button>
    </div>
    <div className="flex flex-wrap items-center gap-1.5">
      {(partner.allowed_sectors || []).map((c) => <Badge key={c} variant="secondary">{LABEL[c] || c}</Badge>)}
      <span className="ml-auto text-xs text-muted-foreground">{partner.users_count} compte(s)</span>
    </div>
    <div className="grid grid-cols-3 gap-2" data-testid={`partner-${partner.slug}-stats`}>
      <Stat label="Dossiers" value={partner.stats?.projects_count ?? 0} testid={`partner-${partner.slug}-stat-count`} />
      <Stat label="Volume CEE" value={fmtMwhc(partner.stats?.total_kwhc || 0)} testid={`partner-${partner.slug}-stat-kwhc`} />
      <Stat label="Prime" value={fmtEur(partner.stats?.total_prime || 0)} testid={`partner-${partner.slug}-stat-prime`} />
    </div>
    <div className="flex items-center justify-between border-t border-border pt-3">
      <StatusToggle partner={partner} onChange={onToggle} />
      {!partner.active && <span className="text-xs text-destructive">Connexions bloquées</span>}
    </div>
  </Card>
);

const MissingPriceBanner = ({ partners }) => {
  if (!partners.length) return null;
  return (
    <div className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm" data-testid="missing-price-banner">
      <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
      <div>
        <div className="font-semibold text-destructive" data-testid="missing-price-banner-title">
          {partners.length} partenaire(s) sans tarif en vigueur
        </div>
        <p className="text-muted-foreground mt-0.5">
          Les simulations de <span className="font-medium text-foreground" data-testid="missing-price-banner-names">{partners.map((p) => p.name).join(", ")}</span> afficheront une prime à 0 €. Configurez une grille active dans <span className="font-semibold text-foreground">Barème de prix</span>.
        </p>
      </div>
    </div>
  );
};

export const AdminPartners = ({ partners, reload }) => {
  const [editing, setEditing] = useState(undefined);
  const [confirm, setConfirm] = useState(null);

  const setStatus = async (p, active) => {
    try {
      await api.patch(`/admin/partners/${p.id}/status`, { active });
      toast.success(active ? `${p.name} réactivé` : `${p.name} désactivé : ${p.users_count} compte(s) bloqué(s)`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-partners">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Le barème de prix de chaque partenaire se règle dans <span className="font-semibold text-foreground">Barème de prix</span> (sélecteur d'espace).</p>
        <Button onClick={() => setEditing(null)} className="bg-genis hover:bg-genis-dark text-white shrink-0" data-testid="admin-add-partner-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Nouveau partenaire
        </Button>
      </div>
      <MissingPriceBanner partners={partners.filter((p) => !p.has_active_pricing)} />
      <div className="grid md:grid-cols-2 gap-4">
        {partners.map((p) => (
          <PartnerCard key={p.id} partner={p} onEdit={() => setEditing(p)} onToggle={(v) => (v ? setStatus(p, true) : setConfirm(p))} />
        ))}
      </div>
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="partner-disable-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Désactiver {confirm?.name} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les {confirm?.users_count} compte(s) de ce partenaire ne pourront plus se connecter et seront déconnectés immédiatement. Leurs simulations sont conservées. Vous pourrez réactiver le partenaire à tout moment.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="partner-disable-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive hover:bg-destructive/90 text-white" onClick={() => { setStatus(confirm, false); setConfirm(null); }} data-testid="partner-disable-confirm">
              Désactiver
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {editing !== undefined && (
        <PartnerDialog partner={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); reload(); }} />
      )}
    </div>
  );
};
